import {useRef, type ReactNode} from "react";
import {motion, useReducedMotion, useTransform, type MotionValue} from "motion/react";

import {CtaButton, CtaLink} from "../CtaButton";
import {RUSTORE_URL} from "../rustore";
import {DownloadIcon, HeartSolidIcon} from "../icons";
import {LANDING_PARALLAX} from "../motion";
import {Reveal, RevealItem} from "../Reveal";
import {useCountUp} from "../useCountUp";
import {useSectionProgress} from "../useSectionProgress";

interface HeroSectionProps {
    openRegister: () => void;
}

/**
 * Первый экран.
 *
 * Появление здесь — по загрузке, а не по прокрутке: к первому экрану никто не
 * прокручивает, он уже на глазах. Порядок разбежки повторяет порядок чтения:
 * заголовок → пояснение → кнопки, и следом карточка анкеты справа.
 * Это не украшение: движение
 * ведёт взгляд по той же дороге, по которой его вёл бы человек, и к моменту,
 * когда взгляд доходит до кнопки, кнопка уже на месте (скилл apple-design §8 —
 * промежуточные кадры должны показывать, куда всё идёт).
 *
 * Дирижирует `.rl-wrap`: сначала левая колонка со своей внутренней очередью,
 * следом — карточка анкеты. Значения пружин — общие для лендинга, `../motion.ts`.
 */
/**
 * Слой параллакса: сдвигается по Y пропорционально проходу первого экрана.
 * Слой — просто обёртка на всю карточку-стопку (`position: absolute; inset: 0`),
 * поэтому внутренняя геометрия карточек, колец и бейджа не меняется; z-index
 * слоя повторяет прежний z-index его содержимого. При «уменьшить движение»
 * `style` не передаётся — слой статичен.
 */
function ParallaxLayer({
    progress,
    shift,
    zIndex,
    reduceMotion,
    children,
}: {
    progress: MotionValue<number>;
    shift: number;
    zIndex: number;
    reduceMotion: boolean;
    children: ReactNode;
}) {
    const y = useTransform(progress, [0, 1], [0, shift]);
    return (
        <motion.div className="rl-layer" style={reduceMotion ? {zIndex} : {y, zIndex}}>
            {children}
        </motion.div>
    );
}

export function HeroSection({openRegister}: HeroSectionProps) {
    const reduceMotion = Boolean(useReducedMotion());
    const sectionRef = useRef<HTMLElement | null>(null);
    const progress = useSectionProgress(sectionRef, !reduceMotion);
    const ringRef = useRef<HTMLDivElement | null>(null);
    useCountUp(ringRef, 92, 0.7);

    return (
        <section className="rl-hero" ref={sectionRef}>
            <div className="rl-glow" aria-hidden/>
            <div className="rl-glow rl-glow-alt" aria-hidden/>
            <Reveal className="rl-wrap" variant="group" trigger="mount" stagger={0.1}>
                <Reveal className="rl-hero-copy" variant="group" trigger="inherit" stagger={0.055}>
                    <RevealItem as="h1" className="rl-hero-title">
                        Найдите того,<br/>с кем <em>совпадёте</em>
                    </RevealItem>

                    <RevealItem as="p" className="rl-lead">
                        Мы считаем совместимость по данным вашей анкеты — характеру, интересам,
                        дате и месту рождения — и показываем её ещё до первого сообщения.
                    </RevealItem>

                    <RevealItem className="rl-hero-cta">
                        <CtaButton className="rl-btn-brand rl-btn-lg" hero onClick={openRegister}>
                            Начать знакомство
                        </CtaButton>
                        <CtaLink href={RUSTORE_URL} className="rl-btn-glass rl-btn-lg" hero>
                            <DownloadIcon className="rl-dl-icon"/>
                            Скачать в RuStore
                        </CtaLink>
                    </RevealItem>
                </Reveal>

                <RevealItem className="rl-hero-art">
                    <ParallaxLayer progress={progress} shift={LANDING_PARALLAX.back} zIndex={1} reduceMotion={reduceMotion}>
                        <div className="rl-pcard rl-b1">
                            <img src="/Landing/profile-2.jpg" alt="" aria-hidden/>
                        </div>
                        <div className="rl-pcard rl-b2">
                            <img src="/Landing/profile-3.jpg" alt="" aria-hidden/>
                        </div>
                    </ParallaxLayer>
                    <ParallaxLayer progress={progress} shift={LANDING_PARALLAX.front} zIndex={3} reduceMotion={reduceMotion}>
                        <div className="rl-pcard rl-front">
                            <img src="/Landing/profile-1.jpg" alt="Пример анкеты в приложении"/>
                            <div className="rl-scrim" aria-hidden/>
                            <div className="rl-meta">
                                <div className="rl-nm">Анна, 28</div>
                                <div className="rl-sub">Москва · в 3 км от вас</div>
                                <div className="rl-chips">
                                    <span>Театр</span>
                                    <span>Походы</span>
                                    <span>Хочет семью</span>
                                </div>
                            </div>
                        </div>
                    </ParallaxLayer>
                    <ParallaxLayer progress={progress} shift={LANDING_PARALLAX.float} zIndex={5} reduceMotion={reduceMotion}>
                        <div className="rl-ring" ref={ringRef}>
                            <i>
                                <b><span data-count>92</span><span>%</span></b>
                                <u>СОВПАДЕНИЕ</u>
                            </i>
                        </div>

                        <div className="rl-heart-badge">
                            <i><HeartSolidIcon/></i>
                            Взаимно
                        </div>
                    </ParallaxLayer>
                </RevealItem>
            </Reveal>
        </section>
    );
}
